import type React from "react";
import { useMemo } from "react";

import { useState, useRef, useEffect } from "react";
import { formatCurrency } from "../utils/helpers";
import { cn } from "../utils/style";

export type Range = {
  min?: number | undefined;
  max?: number | undefined;
};

export type RangeTemplate = {
  id?: string;
  label: string;
  min?: number;
  max?: number;
};

interface RangeFilterProps {
  value?: Range;
  onChange?: (range: Range | undefined) => void;
  templates?: RangeTemplate[];
  className?: string;
}

export function RangeFilter({
  className,
  value,
  onChange,
  templates = [],
}: RangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [min, setMin] = useState<string | undefined>(undefined);
  const [max, setMax] = useState<string | undefined>(undefined);

  useEffect(() => {
    setMin(value?.min === undefined ? value?.min : value.min.toString());
    setMax(value?.max === undefined ? value?.max : value.max.toString());
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const reset = () => {
    setMin(undefined);
    setMax(undefined);
    onChange?.(undefined);
    setIsOpen(false);
  };

  const apply = () => {
    if (onChange) {
      onChange({
        min: min ? parseInt(min) : undefined,
        max: max ? parseInt(max) : undefined,
      });
    }
    setIsOpen(false);
  };

  const renderValueName = ({ min, max }: Range) => {
    if (min !== undefined && max === undefined) {
      return `≧ ${formatCurrency(min)}`;
    }

    if (max !== undefined && min === undefined) {
      return `≦ ${formatCurrency(max)}`;
    }

    return `${formatCurrency(min || 0)} - ${formatCurrency(max || 0)}`;
  };

  const isBothInputValid = useMemo(() => {
    if (isNaN(parseInt(min || "")) && isNaN(parseInt(max || ""))) {
      return false;
    }

    return true;
  }, [min, max]);

  return (
    <div ref={dropdownRef} className="relative inline-block w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          "inline-flex items-center gap-2 px-4 py-2 font-medium text-sm bg-white text-gray-800 rounded-lg hover:bg-gray-100/90",
          "border border-gray-200",
          "focus:outline-none focus:border focus:border-blue-500",
          "transition-colors duration-200",
          className,
        )}
      >
        {value ? renderValueName(value) : "All range"}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 w-96 bg-white text-popover-foreground rounded-lg shadow-lg border border-border z-50 overflow-hidden",
            "origin-bottom",
            "left-0 -translate-x-1/2 ml-[50%]",
          )}
        >
          <div>
            <div className="p-4">
              <div className="text-base font-medium text-gray-800 mb-3">
                Profit Range
              </div>
              <div className="w-full flex items-center gap-2">
                <div className="relative">
                  <input
                    className="pl-16 w-full h-10 border-[1.5px] border-gray-200 p-2 rounded-[4px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                    id="min"
                    type="number"
                    inputMode="numeric"
                    step={1}
                    value={min || ""}
                    placeholder="Min"
                    onChange={(e) => setMin(e.target.value)}
                  />
                  <label
                    htmlFor="min"
                    className="bg-gray-50 border rounded-l-[4px] absolute inset-y-0 left-0 px-3 py-2 flex items-center font-medium text-sm"
                  >
                    Rp.
                  </label>
                </div>
                <div className="relative">
                  <input
                    className="pl-16 w-full h-10 border-[1.5px] border-gray-200 p-2 rounded-[4px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                    id="max"
                    type="number"
                    inputMode="numeric"
                    step={1}
                    value={max || ""}
                    placeholder="Max"
                    onChange={(e) => setMax(e.target.value)}
                  />
                  <label
                    htmlFor="max"
                    className="bg-gray-50 border rounded-l-[4px] absolute inset-y-0 left-0 px-3 py-2 flex items-center font-medium text-sm"
                  >
                    Rp.
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setMin(t.min === undefined ? t.min : t.min.toString());
                      setMax(t.max === undefined ? t.max : t.max.toString());
                    }}
                    className="px-2 py-1 bg-white text-gray-900 border border-gray-300 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between px-4 pb-4 pt-2 border-t border-gray-200">
              <button
                onClick={reset}
                className="h-10  text-gray-500 hover:bg-gray-200 text-sm font-medium px-3 py-2 rounded-md"
              >
                Reset
              </button>
              <button
                disabled={!isBothInputValid}
                onClick={apply}
                className="disabled:opacity-60 disabled:cursor-not-allowed h-10 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
